/**
 * BTX Export component - toolbar button that triggers Excel export
 *
 * This component:
 * - Renders an export button in the toolbar
 * - Persists pending edits, then fetches coverage data when export is triggered
 * - Delegates to ExportRunner for grid rendering and workbook generation
 * - Uses useExportActions for download and SharePoint upload
 */

import { logger } from "@platform/browser-logger";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useNotification } from "@platform/hooks";

import { ExportButton } from "./ExportButton";
import { ExportRunner } from "./ExportRunner";
import { useExportActions } from "./useExportActions";

import type ExcelJS from "exceljs";

interface BTXExportProps {
  rfpName: string;
  ecoRfpId: string;
  /** Persists pending edits; awaited before any export data is fetched. */
  onBeforeExport: () => Promise<void>;
}

/**
 * BTX Export component - handles the complete export workflow
 */
export const BtxExport: FC<BTXExportProps> = ({
  rfpName,
  ecoRfpId,
  onBeforeExport,
}) => {
  const { id: brokerPortalRfpId } = useParams<{ id: string }>();
  const { showError } = useNotification();
  const { downloadExport } = useExportActions({ rfpName });

  const [isExporting, setIsExporting] = useState(false);
  const [hasExportError, setHasExportError] = useState(false);
  const [hasExportSuccess, setHasExportSuccess] = useState(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleExportError = useCallback(
    (error: Error): void => {
      logger.error("[btx export] Export error:", error);
      showError(`Export failed: ${error.message}`);
      setIsExporting(false);
      setHasExportError(true);

      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      errorTimeoutRef.current = setTimeout(() => {
        setHasExportError(false);
        errorTimeoutRef.current = null;
      }, 3000);
    },
    [showError],
  );

  const handleExportComplete = useCallback(
    async (workbook: ExcelJS.Workbook): Promise<void> => {
      await downloadExport(workbook);

      setIsExporting(false);
      setHasExportSuccess(true);

      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => {
        setHasExportSuccess(false);
        successTimeoutRef.current = null;
      }, 3000);
    },
    [downloadExport],
  );

  const [isPreparingExport, setIsPreparingExport] = useState(false);

  const handleExportClick = useCallback(async (): Promise<void> => {
    setIsPreparingExport(true);
    setHasExportError(false);
    setHasExportSuccess(false);

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }

    try {
      await onBeforeExport();
      setIsExporting(true);
    } catch (error) {
      handleExportError(error as Error);
    } finally {
      setIsPreparingExport(false);
    }
  }, [onBeforeExport, handleExportError]);

  useEffect(
    () => () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <>
      <ExportButton
        onClick={handleExportClick}
        isExporting={isPreparingExport || isExporting}
        hasExportError={hasExportError}
        hasExportSuccess={hasExportSuccess}
      />
      {isExporting && brokerPortalRfpId && (
        <ExportRunner
          rfpName={rfpName}
          ecoRfpId={ecoRfpId}
          brokerPortalRfpId={brokerPortalRfpId}
          onComplete={handleExportComplete}
          onError={handleExportError}
        />
      )}
    </>
  );
};
